import { action, observable } from 'mobx';
import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import HttpStatusCode from '@utils/httpStatusCodes';
import { AxiosResponse } from 'axios';
import _ from 'lodash';

export interface ISettingsStore {
  rootStore: IRootStore;
  tokens: ITokenInfo[] | undefined;
  getTokens: () => Promise<void>;
  createToken: (at: ITokenInfo) => Promise<string>;
  deleteToken: (name: string) => Promise<boolean>;
  deleteAllTokens: () => Promise<boolean>;
}

export interface ITokenInfo {
  name: string;
  role: string;
  description?: string;
}

interface IGetTokensResp extends ITokenInfo {
  userId: string;
}

interface ICreateTokenResp {
  success: boolean;
  data: { token: string };
}

export class SettingsStore implements ISettingsStore {
  @observable public rootStore: IRootStore;

  @observable public tokens: ITokenInfo[] | undefined;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  public async getTokens(): Promise<void> {
    const BI_SUCCESS_MESSAGE = 'Personal access token created successfully';
    const BI_FAILURE_MESSAGE = 'Failed to create personal access token';
    const { userStore } = this.rootStore;
    try {
      const resp: AxiosResponse<IGetTokensResp[]> = await apiAuthCaller(userStore.token).get(
        '/accessTokens',
      );
      if (resp.status === HttpStatusCode.Ok) {
        this.tokens = await resp.data.map((token: IGetTokensResp) => _.omit(token, 'userId'));
        window.rudderanalytics.track(BI_SUCCESS_MESSAGE);
      }
    } catch (error) {
      window.rudderanalytics.track(BI_FAILURE_MESSAGE, {
        email: userStore.email,
        error,
      });
    }
  }

  @action.bound
  public async createToken(tokenInfo: ITokenInfo): Promise<string> {
    const BI_SUCCESS_MESSAGE = 'Personal access token created successfully';
    const BI_FAILURE_MESSAGE = 'Failed to create personal access token';
    const { userStore } = this.rootStore;
    try {
      const resp: AxiosResponse<ICreateTokenResp> = await apiAuthCaller(userStore.token).post(
        '/accessTokens',
        tokenInfo,
      );
      if (resp.status === HttpStatusCode.Ok) {
        const { token } = resp.data.data;
        window.rudderanalytics.track(BI_SUCCESS_MESSAGE);
        return token;
      }
      return '';
    } catch (error) {
      window.rudderanalytics.track(BI_FAILURE_MESSAGE, {
        email: userStore.email,
        error,
      });
      return '';
    }
  }

  @action.bound
  public async deleteToken(tokenName: string): Promise<boolean> {
    const BI_SUCCESS_MESSAGE = 'Personal access token deleted successfully';
    const BI_FAILURE_MESSAGE = 'Failed to delete personal access token';
    const { userStore } = this.rootStore;
    try {
      const resp: AxiosResponse<{ success: boolean }> = await apiAuthCaller(userStore.token).delete(
        `/accessTokens?name=${tokenName}`,
      );
      if (resp.status === HttpStatusCode.Ok) {
        window.rudderanalytics.track(BI_SUCCESS_MESSAGE);
        return true;
      }
      return false;
    } catch (error) {
      window.rudderanalytics.track(BI_FAILURE_MESSAGE, {
        email: userStore.email,
        error,
      });
      return false;
    }
  }

  @action.bound
  public async deleteAllTokens(): Promise<boolean> {
    const BI_SUCCESS_MESSAGE = 'All tokens deleted successfully';
    const BI_FAILURE_MESSAGE = 'Failed to delete all tokens';
    const { userStore } = this.rootStore;
    try {
      const resp: AxiosResponse<{ success: boolean }> = await apiAuthCaller(userStore.token).delete(
        '/accessTokens/revokeAll',
      );
      if (resp.status === HttpStatusCode.Ok) {
        window.rudderanalytics.track(BI_SUCCESS_MESSAGE);
        return true;
      }
      return false;
    } catch (error) {
      window.rudderanalytics.track(BI_FAILURE_MESSAGE, {
        email: userStore.email,
        error,
      });
      return false;
    }
  }
}
